import { useState } from "react"
import { recordActivity, upsert } from "../hooks/useQueryData"
import { ConvertDate, TodayDB } from "../utils"
import { ActivitySection } from "../lists/ActivityList"
import { CurrentUser, SESSION_SERVICE } from "../services/SessionService"
import { ActivityRecord } from "../models/Activity"
import useList from "../hooks/useList"
import { merge } from "lodash"
import { SpaceBetween } from "@cloudscape-design/components"
import SegmentedInput from "../widgets/SegmentedInput"
import LabeledTextarea from "../widgets/LabeledTextarea"
import LabeledInput from "../widgets/LabeledInput"
import DateInput from "../widgets/DateInput"
import SelectInput from "../widgets/SelectInput"
import useUserNames from "../hooks/useUserNames"
import ComboInput from "../widgets/ComboInput"
import { useNavigate } from "react-router"
import { ContactRecord } from "../models/Contact"
import { CaseRecord } from "../models/Case"
import Columns from "../widgets/Columns"
import dayjs from "dayjs"
import LabeledPhone from "../widgets/LabeledPhone"

export const QuickCaseTaskSettings = {size: "medium", title: "Quick Case Task"}

const userName = SESSION_SERVICE.getCurrentSession().user?.fullName

export default function QuickCaseTask({values, setMessage, execRef}: {values: any, setMessage: (s: string, t: string) => void, execRef: React.MutableRefObject<() => Promise<string>>}) {
    setMessage("Enter all fields", "info")
    const newQuickCase = {Name: "", State: "",FirstName: "", LastName: "", Phone: "", Email: "", CaseType: "", DateOfLoss: "", AssignedTo: userName, Referral: "", createdBy: userName, updatedBy: userName}
    const [ qc, setCase ] = useState(newQuickCase)
    const navigate = useNavigate()

    let valid = true
    function validate(value: string, label: string): boolean {
        return !!value
        // const thisValid: boolean = value?.length > 0
        // if (!thisValid) {valid = false; setMessage("Please provide values for all required fields")}

        // return thisValid
    }

    async function execute(): Promise<string> {
        // Check for valid values
        if (!qc?.State)      return "ERROR: Please provide a state for this case"
        if (!qc?.FirstName)  return "ERROR: Please provide a first name for this case"
        if (!qc?.FirstName)  return "ERROR: Please provide a first name for this case"
        if (!qc?.LastName)   return "ERROR: Please provide a last name for this case"
        if (!qc?.Phone)      return "ERROR: Please provide a phone number for this client"
        if (qc?.Phone?.startsWith("1")) return "ERROR: Phone number should not start with 1"
        if (qc?.Phone?.length !== 10)   return "ERROR: Please provide a valid 10-digit phone number"
        if (!qc?.Email)      return "ERROR: Please provide an email for this client"
        if (!qc?.CaseType)   return "ERROR: Please provide a type for this case"
        if (!qc?.DateOfLoss) return "ERROR: Please provide a date of loss for this case"
        if (!qc?.AssignedTo) return "ERROR: Please provide a manager for this case"
        if (!qc?.Referral)   return "ERROR: Please provide a referral source for this case"

        // Create Quick Case
        qc.Name = calcName(qc)
        const result = await createCase(qc)
        if (result?.startsWith("ERROR:")) { return result }
        setMessage("Case created successfully", "success")
        navigate("/cases")
        return '' // No error. Successful execution
    }
    execRef.current = execute
    
    const handleChange = (newValues: any) => { 
        const newQC = merge({}, qc, newValues)
        setCase(newQC)
    }
    
    const users = useUserNames().data
    const usersOnly = users?.slice(1)

    return (
        <SpaceBetween direction="vertical" size="m">
            <Columns cols={2} minWidth={200}>
                <div>
                    <b>Calculated Case Name: </b><br/>
                    {calcName(qc) ?? "????????"}
                </div>
                <SelectInput label="State" value={qc?.State} readOnly={false} validate={validate}
                    onChange={ ({detail}: {detail: {value: string}}) => handleChange({State: detail.value}) } 
                    options="WA;OR" />
                <LabeledInput label="Legal First Name" value={qc?.FirstName} validate={validate} readOnly={false}
                    onChange={ ({detail}: {detail: {value: string}}) => handleChange({FirstName: detail.value}) } />
                <LabeledInput label="Legal Last Name" value={qc?.LastName} validate={validate} readOnly={false}
                    onChange={ ({detail}: {detail: {value: string}}) => handleChange({LastName: detail.value}) } />
                <LabeledPhone label="Phone Number" value={qc?.Phone} validate={validate} readOnly={false}
                    onChange={ (s: string) => handleChange({Phone: s}) } />
                <LabeledInput label="Email" value={qc?.Email} validate={validate} readOnly={false}
                    onChange={ ({detail}: {detail: {value: string}}) => handleChange({Email: detail.value}) } />
                <SelectInput label="Case Type" value={qc?.CaseType} readOnly={false} validate={validate}
                    onChange={ ({detail}: {detail: {value: string}}) => handleChange({CaseType: detail.value}) } 
                    options="Motor Vehicle;Slip & Fall;Pedestrian;Motorcycle;Dog Bite;Wrongful Death;Personal Injury" />
                <DateInput label="Date of Loss" validate={validate} readOnly={false}
                    value={qc?.DateOfLoss}
                    onChange={ (value) => handleChange({DateOfLoss: value}) } />
                <SelectInput label="Case Manager" value={qc?.AssignedTo} validate={validate} readOnly={false}
                    options={usersOnly ?? []} onChange={ (v: any) => handleChange({AssignedTo: v.detail.value}) } />
                <ComboInput label="Referral Source" value={qc?.Referral} readOnly={false} validate={validate}
                    options={useList("ReferralSource")}
                    onChange={ (v) => handleChange({Referral: v}) } />
            </Columns>
        </SpaceBetween>
    )
}

function initials(name: string) {
    return name?.split(" ").map((n,i,a)=> i === 0 || i+1 === a.length ? n[0] : null).join("")
}

function calcName(qc: any) {
    const acronyms: { [key: string]: string; } = {
        "Motor Vehicle": "MVA",
        "Slip & Fall": "SF", 
        "Pedestrian": "PED",
        "Motorcycle": "MC",
        "Dog Bite": "DB",
        "Wrongful Death": "WD",
        "Personal Injury": "PI",
      }

    const caseInitials = acronyms[qc?.CaseType] ?? ""
    let name = (qc?.LastName ? qc?.LastName : "") + ", " + (qc?.FirstName ? qc?.FirstName : "")
    name += " - " + caseInitials + " " + (qc?.DateOfLoss ? ConvertDate(qc?.DateOfLoss) : "")
    const caseManager = initials(qc?.AssignedTo)
    const referralSource = initials(qc?.Referral)
    name += " | " + caseManager + " | " + referralSource

    return name
}

    async function createCase(qc: any) {           
        const currentUser = CurrentUser().fullName

        if (!window.confirm("Are you sure you want to create this Case? Please allow time for this process to run. Do not click the Create Case button a second time.")) { return }

        let solYears = 2
        if (qc.State === "WA") { solYears = 3 }

        const contactRecord: any = {
            createdBy: currentUser, 
            updatedBy: currentUser,
            Type: "Client",
            FirstName: qc.FirstName,
            LastName: qc.LastName,
            Phone: qc.Phone,
            Email: qc.Email,
        }

        const contactId = await upsert('Contact', contactRecord)
        if (!contactId) { return "ERROR: Contact creation failed" }

        const summaryId = await upsert('CaseSummary', {createdBy: 'System', updatedBy: 'System'})
        if (!summaryId) { return "ERROR: CaseSummary creation failed" }

                const caseRecord: CaseRecord = {
                    createdBy: currentUser, 
                    updatedBy: currentUser,
                    Name: qc.Name,
                    Client: contactId,
                    Summary: summaryId, // CaseSummary created first
                    AssignedTo: qc.AssignedTo,
                    Status: "Intake",
                    Details: {
                        CollisionInformation: {DateOfLoss: ConvertDate(qc.DateOfLoss), StatuteOfLimitations: dayjs(qc.DateOfLoss).add(solYears, 'years').format('YYYY-MM-DD')},
                        LawEnforcement: {},
                        LegalRepresentation: {Manager: qc.AssignedTo, Referral: qc.Referral},
                        FactsOfLoss: {CaseType: qc.CaseType},
                        FirstPartyVehicle: {},
                        FirstPartyInsurance: {},
                        FirstPartyAdjuster: {},
                        ThirdPartyVehicle: {},
                        ThirdPartyInsurance: {},
                        ThirdPartyAdjuster: {},
                        AtFault: {}
                    } as any} as any
    
                const newCaseId: string = (await upsert('Case', caseRecord))
                if (!newCaseId) { return "ERROR: Case creation failed" }

                recordActivity('Contact', 'Initial Consultation', newCaseId, 'Case', newCaseId)

                await upsert('CaseSummary', {_id: summaryId, CaseId: newCaseId})                
        }
